import { useState, useCallback, useEffect } from 'react'
import api from '../../../services/api'

const useAiCreditCosts = (initialFilter = '5', defaultOrg = null) => {
  const [dateFilter, setDateFilter] = useState(initialFilter)
  const [dateFrom, setDateFrom] = useState('')
  const [dateTo, setDateTo] = useState('')
  const [selectedOrg, setSelectedOrg] = useState(defaultOrg)
  const [orgList, setOrgList] = useState([])

  // Costs table state
  const [searchTerm, setSearchTerm] = useState('')
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
  const [tableFilter, setTableFilter] = useState('All Time')
  const [selectedType, setSelectedType] = useState('ai-image')
  const [offset, setOffset] = useState(0)
  const [limit, setLimit] = useState(10)
  const [userData, setUserData] = useState([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState(null)
  const [total, setTotal] = useState(0)

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm)
    }, 300)
    return () => clearTimeout(timer)
  }, [searchTerm])

  const handleDateFilterChange = useCallback(
    value => {
      setTableFilter(value)
      setOffset(0)
      if (value !== 'Other') {
        setDateFrom('')
        setDateTo('')
      }
    },
    []
  )

  const handleDateRangeChange = useCallback(
    (dates, dateStrings) => {
      if (dateStrings && Array.isArray(dateStrings) && dateStrings.length === 2) {
        setDateFrom(dateStrings[0])
        setDateTo(dateStrings[1])
        setOffset(0)
      } else if (dates && Array.isArray(dates) && dates.length === 2) {
        setDateFrom(dates[0] ? dates[0].format('YYYY-MM-DD') : '')
        setDateTo(dates[1] ? dates[1].format('YYYY-MM-DD') : '')
        setOffset(0)
      } else {
        setDateFrom('')
        setDateTo('')
      }
    },
    []
  )

  const handleSearch = useCallback((value) => {
    setSearchTerm(value)
    setOffset(0)
  }, [])

  const handlePageChange = useCallback((page, pageSize) => {
    setOffset((page - 1) * pageSize)
    setLimit(pageSize)
  }, [])

  const fetchUsers = useCallback(async () => {
    if (!selectedType) {
      setUserData([])
      setTotal(0)
      setLoading(false)
      return
    }

    setLoading(true)
    setError(null)
    setUserData([])

    const mapFilter = (label) => {
      switch (label) {
        case 'All Time': return 'allTime'
        case 'Today': return 'today'
        case 'This Week': return 'week'
        case 'This Month': return 'month'
        case 'This Year': return 'year'
        default: return ''
      }
    }

    const selectedDateFilter = mapFilter(tableFilter) ||
      (dateFilter === 'other' || dateFilter === '2' || dateFilter === '5' ? 'allTime' : dateFilter)

    try {
      const response = await api.reports.getUsageReportCosts({
        offset,
        limit,
        type: selectedType,
        search: debouncedSearchTerm,
        date: selectedDateFilter,
        from: dateFrom,
        to: dateTo,
        ...(selectedOrg ? { org_id: selectedOrg } : {})
      })

      const responseData = response?.data || response || {}
      const rawRecords = responseData.result || []

      const mappedData = Array.isArray(rawRecords) ? rawRecords.map(item => {
        const userObj = item.user
        const creatorName = userObj ? `${userObj.first_name || ''} ${userObj.last_name || ''}`.trim() : null
        const displayName = creatorName || 'Unknown'
        const displayAvatar = userObj?.avatar?.link || ''
        return {
          id: item.id,
          name: displayName,
          email: userObj?.email || '',
          avatar: displayAvatar,
          totalCreditsUsed: item.avg_cost || 0,
          totalCost: item.total_cost || 0,
          total_credit_cost: item.total_credit_cost || 0,
          model: item.model,
          title: item.title || 'N/A',
          depth: item.depth || 0,
          organization: item.organization || 'N/A',
          duration: Math.round(Number(item.duration || 0)),
          total_pages: item.total_pages || 0,
          total_tokens_cost: item.total_tokens_cost || 0,
          elearningCourse: item.elearning_course || 'N/A',
          user_prompt: item.user_prompt || ''
        }
      }) : []

      setUserData(mappedData)
      setTotal(responseData.total || 0)
      setLoading(false)
    } catch (err) {
      console.error('Error fetching cost report data:', err)
      setError(err.message || 'Failed to fetch data')
      setLoading(false)
    }
  }, [dateFilter, dateFrom, dateTo, debouncedSearchTerm, tableFilter, offset, limit, selectedType, selectedOrg])

  useEffect(() => {
    fetchUsers()
  }, [fetchUsers])

  const fetchOrganizations = useCallback(async () => {
    try {
      const response = await api.organizations.get({ limit: 1000 })
      const rows = response?.data?.rows || []
      setOrgList(rows)
    } catch (error) {
      console.error('Error fetching organizations:', error)
    }
  }, [])

  useEffect(() => {
    fetchOrganizations()
  }, [fetchOrganizations])

  return {
    tableFilter,
    setTableFilter: handleDateFilterChange,
    dateFrom,
    setDateFrom,
    dateTo,
    setDateTo,
    userData,
    userLoading: loading,
    userError: error,
    pagination: {
      current: Math.floor(offset / limit) + 1,
      pageSize: limit,
      total: total
    },
    searchTerm,
    onSearch: handleSearch,
    onPageChange: handlePageChange,
    refetchUsers: fetchUsers,
    selectedType,
    setSelectedType: (type) => {
      setSelectedType(type)
      setOffset(0)
    },
    selectedOrg,
    setSelectedOrg: (org) => {
      setSelectedOrg(org)
      setOffset(0)
    },
    orgList,
    setDateRange: handleDateRangeChange
  }
}

export default useAiCreditCosts
